import { Edit } from '@mui/icons-material';
import { Grid2, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Tax } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useGetSalesTaxesListQuery, useTaxesMutation } from 'api/hooks/billing/tax';
import { CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { DATEFORMATWITHTIME } from 'app/containers/Billing/utils';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { TaxDrawer } from './TaxDrawer';

export const TaxTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'key', sort: 'asc' }],
    defaultPageSize: 100,
    sortKey: 'tax_sort',
    paginationKeys: { page: 'tax_page', pageSize: 'tax_pageSize' }
  });

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tax, setTax] = useState<Tax | undefined>(undefined);

  const { data, isLoading, isFetching } = useGetSalesTaxesListQuery({
    params: {
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const { creation, update } = useTaxesMutation();

  const handleSaveClick = async value => {
    try {
      if (isSubmitting) {
        return;
      }
      if (tax?.id) {
        await update.mutateAsync({ key: value.key, data: value });
        dispatch(
          AlertActions.success(t(translations.pages.billingSettings.mutation.taxUpdateSuccess()))
        );
      } else {
        await creation.mutateAsync(value);
        dispatch(
          AlertActions.success(t(translations.pages.billingSettings.mutation.taxCreationSuccess()))
        );
      }
      setOpen(false);
    } catch (error) {
      if (tax?.id) {
        dispatch(
          AlertActions.error(t(translations.pages.billingSettings.mutation.taxUpdateFailed()))
        );
      } else {
        AlertActions.error(t(translations.pages.billingSettings.mutation.taxCreationFailed()));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setTax(params.row);
            setOpen(true);
          }}
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Tax> => {
    return [
      { field: 'key', flex: 2, headerName: t(translations.pages.billingSettings.tax.key()) },
      { field: 'text', flex: 2, headerName: t(translations.pages.billingSettings.tax.text()) },
      {
        field: 'taxRate',
        flex: 2,
        headerName: t(translations.pages.billingSettings.tax.taxRate())
      },
      {
        field: 'updatedAt',
        flex: 1,
        headerName: t(translations.common.updatedAt()),
        renderCell: params => (
          <Typography variant="body2">
            {format(new Date(params.row.updatedAt), DATEFORMATWITHTIME, { locale: de })}
          </Typography>
        )
      },
      {
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  return (
    <Grid2 style={{ flexGrow: 1 }}>
      <TaxDrawer open={open} setOpen={setOpen} tax={tax} handleSaveClick={handleSaveClick} />
      <Grid2 container justifyContent={'space-between'} paddingBottom={1}>
        <Searchfield handleSearch={handleSearchTextChange} search={search} />
        <CreateButton
          renderSkeleton={isLoading}
          onClick={() => {
            setTax(undefined);
            setOpen(true);
          }}
        />
      </Grid2>
      <Grid2 container style={{ height: '80vh' }}>
        <DataGrid
          loading={isFetching}
          columns={columns}
          rows={data?.data || []}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          sortingMode="server"
          sortModel={sortingModel}
          onSortModelChange={handleSortModelChange}
          rowCount={data?.count || 0}
        />
      </Grid2>
    </Grid2>
  );
};
